import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Tooltip,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSharedAuth } from "../contexts/AuthProvider";
import { t } from "@lingui/macro";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion"; // ← icon for interactive badge

const StoryCard = ({ story }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { customClaims } = useSharedAuth();

  const handleNavigate = () => {
    if (customClaims?.isEditor) {
      navigate(`/editorial/stories/${story.id}`);
    } else {
      navigate(`/mystories/${story.id}`);
    }
  };

  const isInteractive = story?.type === "interactive" || story?.isInteractive === true;

  return (
    <Card
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: 140,
        height: 238,
        boxShadow: theme.shadows[5],
        borderRadius: 1,
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        "&:hover": { boxShadow: theme.shadows[8], transform: "scale(1.05)" },
        margin: 0,
        padding: 0,
      }}
    >
      {/* Published/Draft badge (top-right) */}
      <Box
        sx={{
          position: "absolute",
          top: theme.spacing(0.5),
          right: theme.spacing(0.5),
          backgroundColor: story.isPublished ? theme.palette.success.main : theme.palette.warning.main,
          borderRadius: 15,
          px: 0.5,
          py: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: theme.shadows[2],
          zIndex: 2,
          minWidth: "40px",
          border: "1px solid white",
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: 8,
            fontWeight: theme.typography.fontWeightBold,
            color: "white",
            textTransform: "uppercase",
            textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
            m: 0.25,
            lineHeight: 1,
          }}
        >
          {story.isPublished ? t({ message: "PUBLISHED" }) : t({ message: "DRAFT" })}
        </Typography>
      </Box>

      {/* Interactive badge (top-left) — only for interactive stories */}
      {isInteractive && (
        <Tooltip title={t({ message: "Interactive Story" })} placement="top" arrow>
          <Box
            sx={{
              position: "absolute",
              top: theme.spacing(0.5),
              left: theme.spacing(0.5),
              backgroundColor: theme.palette.info.main,
              borderRadius: 15,
              height: 20,
              minWidth: 20,
              px: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: theme.shadows[2],
              zIndex: 2,
              border: "1px solid white",
              pointerEvents: "none", // let clicks pass through to CardActionArea
            }}
          >
            <AutoAwesomeMotionIcon sx={{ fontSize: 14, color: "white" }} />
          </Box>
        </Tooltip>
      )}

      <Tooltip title={story.description || t({ message: "No description available" })} placement="top" arrow>
        <CardActionArea onClick={handleNavigate} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <CardMedia
            component="img"
            image={story.coverImageUrl || story.coverUrl || "/book_cover_placeholder.webp"}
            alt={t({ message: "{title} Cover", values: { title: story.title } })}
            sx={{ height: "100%", width: "100%", objectFit: "cover" }}
            onError={(e) => {
              if (e.target.src !== `${window.location.origin}/book_cover_placeholder.webp`) {
                e.target.src = "/book_cover_placeholder.webp";
              }
            }}
          />

          <CardContent
            sx={{
              height: "20%",
              width: "100%",
              p: theme.spacing(0.5),
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              flex: 1,
              pl: theme.spacing(0.5),
              pr: 0,
            }}
          >
            <Typography
              variant="h7"
              component="div"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
            >
              {story.title}
            </Typography>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
            >
              {story.writerName || t({ message: "Unknown Author" })}
            </Typography>
          </CardContent>
        </CardActionArea>
      </Tooltip>
    </Card>
  );
};

export default StoryCard;
